/**
 * authErrorPresenter.ts
 * ---------------------
 * Maps decided actions to user-facing UI: toasts (react-toastify works outside
 * React) and the blocking "auth service down" modal (via the UI bus).
 */
import { toast } from 'react-toastify';
import Auth from '@utils/auth';
import config from '@config/config';
import { authUiBus } from './authUiBus';
import {
  AUTH_MODAL_TITLES,
  AUTH_MODAL_MESSAGES,
  AUTH_TOAST_MESSAGES,
} from '@constants/authErrorConstants';

const ref = (id?: string) => (id ? ` (Ref: ${id})` : '');

/**
 * Requirement: refresh permanently failed -> blocking modal; on OK, log out.
 * Logout mirrors the existing refresh-failure flow (clear session + redirect to
 * the basename-aware /logout route).
 */
export function showAuthServiceDownModal(correlationId?: string): void {
  authUiBus.emitModal({
    title: AUTH_MODAL_TITLES.AUTH_SERVICE_DOWN,
    message: AUTH_MODAL_MESSAGES.AUTH_SERVICE_DOWN,
    correlationId,
    onOk: () => {
      try {
        Auth.logOut();
      } finally {
        window.location.href = `${config.root ? config.root : ''}/logout`;
      }
    },
  });
}

export function showModalforMultiDeviceRestriction(correlationId?: string): void {
  authUiBus.emitModal({
    title: AUTH_MODAL_TITLES.MULTI_DEVICE_RESTRICTION,
    message: AUTH_MODAL_MESSAGES.MULTI_DEVICE_RESTRICTION,
    correlationId,
    onOk: () => {
      try {
        Auth.logOut();
      } finally {
        window.location.href = `${config.root ? config.root : ''}/logout`;
      }
    },
  });
}

/**
 * Requirement: for every forced "back to login" case (corrupted/invalid token,
 * session expired, session invalidated) show a blocking popup FIRST. Only after
 * the user acknowledges (OK) do we clear the local token and redirect to login.
 * We never redirect before the popup is shown.
 */
export function showLoginRequiredModal(description?: string, correlationId?: string): void {
  const message =
    (description && AUTH_MODAL_MESSAGES.LOGIN_REQUIRED[description]) ??
    AUTH_MODAL_MESSAGES.LOGIN_REQUIRED_FALLBACK;
  authUiBus.emitModal({
    title: AUTH_MODAL_TITLES.SESSION_ENDED,
    message,
    correlationId,
    onOk: () => {
      try {
        // Clear the corrupted/expired local token before re-authenticating.
        Auth.logOut();
      } finally {
        window.location.href = `${config.root ? config.root : ''}/logout`;
      }
    },
  });
}

/** 500 unknown (db etc.) -> non-auth "service unavailable" toast. */
export function showServiceUnavailableToast(correlationId?: string): void {
  toast.error(`${AUTH_TOAST_MESSAGES.SERVICE_UNAVAILABLE}${ref(correlationId)}`, {
    position: 'top-center',
  });
}

/** 500 known auth-internal -> retryable platform toast. */
export function showPlatformErrorToast(correlationId?: string): void {
  toast.error(`${AUTH_TOAST_MESSAGES.PLATFORM_ERROR}${ref(correlationId)}`, {
    position: 'top-center',
  });
}
